import { useQuery } from '@tanstack/react-query';
import { getPageBySlug, getPosts, getPostBySlug, WordPressNotConfiguredError } from '../api/client';
import type { Page, Post } from '../api/client';
import { getEventBySlug, getEvents, getFaqs, getGallery, getTeam, getTerms, getTestimonials } from '../api/wp-content';
import type { Faq, GalleryItem, GetEventsOptions, TeamMember, Term, Testimonial, WpEvent } from '../api/wp-content';

// Don't retry when WordPress simply isn't configured (blank VITE_API_BASE_URL) —
// it's a permanent state, not a transient failure. Retry other errors up to 2×.
const wpRetry = (failureCount: number, error: unknown) =>
  !(error instanceof WordPressNotConfiguredError) && failureCount < 2;

// Query key factory for cache invalidation and prefetching
export const wordpressKeys = {
  all: ['wordpress'] as const,
  pages: () => [...wordpressKeys.all, 'page'] as const,
  page: (slug: string) => [...wordpressKeys.pages(), slug] as const,
  posts: () => [...wordpressKeys.all, 'posts'] as const,
  postList: (perPage: number) => [...wordpressKeys.posts(), { perPage }] as const,
  post: (slug: string) => [...wordpressKeys.posts(), slug] as const,
  // Content-model post types (PRP-11)
  content: () => [...wordpressKeys.all, 'content'] as const,
  testimonials: (limit: number, term?: string) =>
    [...wordpressKeys.content(), 'testimonials', { limit, term: term ?? null }] as const,
  team: () => [...wordpressKeys.content(), 'team'] as const,
  faqs: () => [...wordpressKeys.content(), 'faqs'] as const,
  gallery: (term: string | undefined, limit: number) =>
    [...wordpressKeys.content(), 'gallery', { term: term ?? null, limit }] as const,
  terms: () => [...wordpressKeys.content(), 'terms'] as const,
  events: (opts: GetEventsOptions) =>
    [...wordpressKeys.content(), 'events', { upcoming: opts.upcoming ?? true, term: opts.term ?? null, limit: opts.limit ?? 20 }] as const,
  event: (slug: string) => [...wordpressKeys.content(), 'event', slug] as const,
};

export const useWordPressPage = (slug: string, options?: { enabled?: boolean }) =>
  useQuery<Page>({
    queryKey: wordpressKeys.page(slug),
    queryFn: () => getPageBySlug(slug),
    enabled: (options?.enabled ?? true) && !!slug,
    retry: wpRetry,
  });

export const useWordPressPosts = (perPage = 10) =>
  useQuery<Post[]>({
    queryKey: wordpressKeys.postList(perPage),
    queryFn: () => getPosts(1, perPage),
    retry: wpRetry,
  });

export const useWordPressPost = (slug: string) =>
  useQuery<Post>({
    queryKey: wordpressKeys.post(slug),
    queryFn: () => getPostBySlug(slug),
    enabled: !!slug,
    retry: wpRetry,
  });

// ============ Content-model hooks (PRP-11) ============
// Each resolves to [] when WordPress or the content engine is absent, so
// components can treat "pending / error / empty" alike and show their fallback.

export const useWpTestimonials = (limit = 3, term?: string) =>
  useQuery<Testimonial[]>({
    queryKey: wordpressKeys.testimonials(limit, term),
    queryFn: () => getTestimonials(limit, term),
    retry: wpRetry,
  });

export const useWpTeam = () =>
  useQuery<TeamMember[]>({ queryKey: wordpressKeys.team(), queryFn: getTeam, retry: wpRetry });

export const useWpFaqs = () =>
  useQuery<Faq[]>({ queryKey: wordpressKeys.faqs(), queryFn: getFaqs, retry: wpRetry });

export const useWpGallery = (term?: string, limit = 12) =>
  useQuery<GalleryItem[]>({
    queryKey: wordpressKeys.gallery(term, limit),
    queryFn: () => getGallery(term, limit),
    retry: wpRetry,
  });

export const useWpTerms = () =>
  useQuery<Term[]>({ queryKey: wordpressKeys.terms(), queryFn: getTerms, retry: wpRetry });

export const useWpEvents = (opts: GetEventsOptions = {}) =>
  useQuery<WpEvent[]>({ queryKey: wordpressKeys.events(opts), queryFn: () => getEvents(opts), retry: wpRetry });

export const useWpEvent = (slug: string) =>
  useQuery<WpEvent | null>({
    queryKey: wordpressKeys.event(slug),
    queryFn: () => getEventBySlug(slug),
    enabled: !!slug,
    retry: wpRetry,
  });
